import { onIsLoading } from '../../utils/onLoading'
import {
  PageCenteredSection,
  PageContentShell,
  PageViewport,
} from '../../components/PageLayout'
import { getTopbarHeight, useDimensions } from '../../hooks/useDimensions'
import { FooterBarComponent } from '../FooterBar'
import { getIsModalOpen, getLoginForm } from './selectors'
import { actions, LoginFormValues } from './reducer'
import { actions as appActions } from '../App/reducer'
import { ACTIVATE_ACCOUNT_ROUTE, REGEX_EMAIL } from '../../config'
import { useDispatch, useSelector } from 'react-redux'
import { useFirebase } from 'react-redux-firebase'
import React from 'react'
import Firebase from 'firebase/compat/app'
import 'firebase/compat/auth'
import Button from '@mui/material/Button'
import Avatar from '@mui/material/Avatar'
import Link from '@mui/material/Link'
import { IconButton, InputAdornment, TextField, Theme } from '@mui/material'
import Card from '@mui/material/Card'
import CardActions from '@mui/material/CardActions'
import CardContent from '@mui/material/CardContent'
import CardHeader from '@mui/material/CardHeader'
import Box from '@mui/material/Box'
import Visibility from '@mui/icons-material/Visibility'
import VisibilityOff from '@mui/icons-material/VisibilityOff'
import EmailIcon from '@mui/icons-material/Email'
import GoogleIcon from '@mui/icons-material/Google'
import SendIcon from '@mui/icons-material/Send'
import FormatClearIcon from '@mui/icons-material/FormatClear'
import Logo from '../../assets/logo.png'
import { LoginMultiFactorDialog } from './MultiFactorDialog'
import { ResetModalComponent } from './Reset'
import { Helmet } from 'react-helmet'
import { AppBarComponent } from '../AppBar'
import { LoginCardSurface } from './styled'
import {
  getFriendlyFirebaseErrorMessage,
  isMultiFactorError,
  isPhoneMultiFactorInfo,
} from '../../utils/firebaseMultiFactor'

export const LoginComponent: React.FC = () => {
  const dispatch = useDispatch()
  const firebase = useFirebase()

  const [showPassword, setShowPassword] = React.useState(false)
  const [emailNotValid, setEmailNotValid] = React.useState(false)
  const [mfaResolver, setMfaResolver] =
    React.useState<Firebase.auth.MultiFactorResolver | null>(null)
  const [mfaHints, setMfaHints] = React.useState<
    Firebase.auth.MultiFactorInfo[]
  >([])
  const [selectedMfaHintUid, setSelectedMfaHintUid] = React.useState('')
  const [mfaVerificationId, setMfaVerificationId] = React.useState<
    string | null
  >(null)
  const [mfaVerificationCode, setMfaVerificationCode] = React.useState('')
  const [mfaErrorMessage, setMfaErrorMessage] = React.useState<string | null>(
    null
  )
  const [isSendingMfaCode, setIsSendingMfaCode] = React.useState(false)
  const [isResolvingMfa, setIsResolvingMfa] = React.useState(false)
  const { height, width } = useDimensions()
  const topbarHeight = getTopbarHeight(width)
  const mfaRecaptchaContainerId = React.useMemo(
    () => `login-mfa-recaptcha-${Math.random().toString(36).slice(2)}`,
    []
  )
  const mfaRecaptchaVerifierRef =
    React.useRef<Firebase.auth.RecaptchaVerifier | null>(null)

  const loginForm = useSelector(getLoginForm)
  const isModalOpen = useSelector(getIsModalOpen)

  const { email, password } = loginForm

  React.useEffect(
    () => () => {
      try {
        mfaRecaptchaVerifierRef.current?.clear()
      } catch {
        return
      } finally {
        mfaRecaptchaVerifierRef.current = null
      }
      dispatch(actions.onLoginForm(LoginFormValues))
    },
    [dispatch]
  )

  const clearMfaVerifier = React.useCallback(() => {
    try {
      mfaRecaptchaVerifierRef.current?.clear()
    } catch {
      return
    } finally {
      mfaRecaptchaVerifierRef.current = null
    }
  }, [])

  const resetMfaState = React.useCallback(() => {
    setMfaResolver(null)
    setMfaHints([])
    setSelectedMfaHintUid('')
    setMfaVerificationId(null)
    setMfaVerificationCode('')
    setMfaErrorMessage(null)
    setIsSendingMfaCode(false)
    setIsResolvingMfa(false)
    clearMfaVerifier()
  }, [clearMfaVerifier])

  const selectedMfaHint = React.useMemo(
    () =>
      mfaHints.find((hint) => hint.uid === selectedMfaHintUid) ??
      mfaHints[0] ??
      null,
    [mfaHints, selectedMfaHintUid]
  )

  const createMfaRecaptchaVerifier = React.useCallback(async () => {
    if (mfaRecaptchaVerifierRef.current != null) {
      return mfaRecaptchaVerifierRef.current
    }

    const verifier = new Firebase.auth.RecaptchaVerifier(
      mfaRecaptchaContainerId,
      { size: 'normal' },
      Firebase.app()
    )

    await verifier.render()

    mfaRecaptchaVerifierRef.current = verifier
    return verifier
  }, [mfaRecaptchaContainerId])

  const onMultiFactorRequired = React.useCallback(
    (error: Firebase.auth.MultiFactorError) => {
      const nextHints = error.resolver.hints

      setMfaResolver(error.resolver)
      setMfaHints(nextHints)
      setSelectedMfaHintUid(nextHints[0]?.uid ?? '')
      setMfaVerificationId(null)
      setMfaVerificationCode('')
      setMfaErrorMessage(
        nextHints.some(isPhoneMultiFactorInfo)
          ? null
          : 'This account requires a second factor that is not supported by this client build.'
      )
      onIsLoading(false)
    },
    []
  )

  const onIsModalOpen = React.useCallback(
    (val: boolean) => dispatch(actions.onIsModalOpen(val)),
    [dispatch]
  )

  const onShowPassword = React.useCallback(
    () => setShowPassword((show) => !show),
    [setShowPassword]
  )

  const onMouseDownPassword = React.useCallback(
    (event: React.MouseEvent<HTMLButtonElement>) => {
      event.preventDefault()
    },
    []
  )

  const onChange = React.useCallback(
    (e: React.ChangeEvent<{ value: unknown }>, prop: string) => {
      dispatch(actions.onLoginForm({ ...loginForm, [prop]: e.target.value }))
    },
    [dispatch, loginForm]
  )

  const onClear = React.useCallback(() => {
    dispatch(actions.onLoginForm(LoginFormValues))
    setEmailNotValid(false)
  }, [dispatch, setEmailNotValid])

  const onLoginError = React.useCallback(
    (message = 'Failed to login') => {
      dispatch(
        appActions.onAlert({
          severity: 'error',
          message,
        })
      )
      onIsLoading(false)
      dispatch(actions.onLoginForm(LoginFormValues))
    },
    [dispatch]
  )

  const onCloseMultiFactorDialog = React.useCallback(() => {
    resetMfaState()
    onIsLoading(false)
  }, [resetMfaState])

  const onSendMultiFactorCode = React.useCallback(async () => {
    if (mfaResolver == null || selectedMfaHint == null) {
      setMfaErrorMessage('Sign in again to restart two-step verification.')
      return
    }

    if (!isPhoneMultiFactorInfo(selectedMfaHint)) {
      setMfaErrorMessage(
        'This account requires a second factor that is not supported by this client build.'
      )
      return
    }

    setIsSendingMfaCode(true)
    setMfaErrorMessage(null)

    try {
      const phoneAuthProvider = new Firebase.auth.PhoneAuthProvider()
      const verifier = await createMfaRecaptchaVerifier()
      const verificationId = await phoneAuthProvider.verifyPhoneNumber(
        {
          multiFactorHint: selectedMfaHint,
          session: mfaResolver.session,
        },
        verifier
      )

      setMfaVerificationId(verificationId)
    } catch (error) {
      setMfaErrorMessage(
        getFriendlyFirebaseErrorMessage(
          error,
          'Unable to send the verification code.'
        )
      )
      clearMfaVerifier()
    } finally {
      setIsSendingMfaCode(false)
    }
  }, [
    clearMfaVerifier,
    createMfaRecaptchaVerifier,
    mfaResolver,
    selectedMfaHint,
  ])

  const onConfirmMultiFactorCode = React.useCallback(async () => {
    if (mfaResolver == null || mfaVerificationId == null) {
      setMfaErrorMessage('Request a verification code before continuing.')
      return
    }

    setIsResolvingMfa(true)
    setMfaErrorMessage(null)

    try {
      const credential = Firebase.auth.PhoneAuthProvider.credential(
        mfaVerificationId,
        mfaVerificationCode.trim()
      )
      const assertion =
        Firebase.auth.PhoneMultiFactorGenerator.assertion(credential)

      await mfaResolver.resolveSignIn(assertion)
      resetMfaState()
      onIsLoading(false)
    } catch (error) {
      setMfaErrorMessage(
        getFriendlyFirebaseErrorMessage(
          error,
          'Unable to verify the security code.'
        )
      )
    } finally {
      setIsResolvingMfa(false)
    }
  }, [mfaResolver, mfaVerificationCode, mfaVerificationId, resetMfaState])

  const onSubmit = React.useCallback(() => {
    onIsLoading(true)
    firebase
      .auth()
      .setPersistence(Firebase.auth.Auth.Persistence.SESSION)
      .then(() => {
        firebase
          .auth()
          .signInWithEmailAndPassword(loginForm.email, loginForm.password)
          .catch((error) => {
            if (isMultiFactorError(error)) {
              onMultiFactorRequired(error)
              return
            }

            console.log('Error!', error.message)
            onLoginError(
              getFriendlyFirebaseErrorMessage(error, 'Failed to login')
            )
          })
      })
      .catch((error) => {
        if (isMultiFactorError(error)) {
          onMultiFactorRequired(error)
          return
        }

        onLoginError(getFriendlyFirebaseErrorMessage(error, 'Failed to login'))
      })
  }, [firebase, loginForm, onLoginError, onMultiFactorRequired])

  const onGoogleSignIn = React.useCallback(() => {
    const provider = new Firebase.auth.GoogleAuthProvider()
    provider.setCustomParameters({ prompt: 'select_account' })

    onIsLoading(true)
    firebase
      .auth()
      .setPersistence(Firebase.auth.Auth.Persistence.SESSION)
      .then(() => firebase.auth().signInWithPopup(provider))
      .catch((error: { code?: string; message?: string }) => {
        if (isMultiFactorError(error)) {
          onMultiFactorRequired(error)
          return
        }

        if (error?.code === 'auth/account-exists-with-different-credential') {
          onLoginError(
            'This email already uses password login. Sign in with your password to keep using the same account.'
          )
          return
        }

        console.log('Error!', error?.message ?? 'Google sign-in failed')
        onLoginError(
          getFriendlyFirebaseErrorMessage(error, 'Failed to login with Google')
        )
      })
  }, [firebase, onLoginError, onMultiFactorRequired])

  const disabled = emailNotValid || !email || !password

  return (
    <>
      <Helmet>
        <title>login || JustLockIt</title>
        <meta name="keywords" content="HTML,CSS,JavaScript" />
        <meta
          name="description"
          content="justlockit.com login page. storage units Schulenburg, Texas"
        />
      </Helmet>
      <AppBarComponent />
      <PageViewport className="login-page" viewportHeight={height}>
        <PageContentShell
          className="login-page__shell"
          topbarHeight={topbarHeight}
        >
          <PageCenteredSection className="login-page__content">
            <LoginCardSurface className="login-page__card-surface">
              <Card className="login-page__card" variant="outlined">
                <CardHeader
                  className="login-page__card-header"
                  avatar={
                    <Avatar aria-label="recipe">
                      <img
                        style={{ width: 45, height: 45 }}
                        alt="logo"
                        src={Logo}
                      />
                    </Avatar>
                  }
                  title="JustLockIt"
                  subheader="Customer Login"
                />
                <CardContent
                  className="login-page__card-body"
                  sx={{
                    backgroundColor: (theme: Theme) => theme.palette.grey[900],
                  }}
                >
                  <TextField
                    label="Email"
                    fullWidth
                    value={email}
                    onChange={(e) => onChange(e, 'email')}
                    error={emailNotValid}
                    helperText={emailNotValid ? 'Email not valid' : ''}
                    onBlur={() =>
                      email && setEmailNotValid(!REGEX_EMAIL.test(email))
                    }
                    InputProps={{
                      endAdornment: (
                        <InputAdornment position="end">
                          <EmailIcon />
                        </InputAdornment>
                      ),
                    }}
                    sx={{ mb: 2 }}
                  />
                  <TextField
                    label="Password"
                    fullWidth
                    value={password}
                    onChange={(e) => onChange(e, 'password')}
                    onKeyDown={(e) =>
                      e.key === 'Enter' && !disabled && onSubmit()
                    }
                    type={showPassword ? 'text' : 'password'}
                    InputProps={{
                      endAdornment: (
                        <InputAdornment position="end">
                          <IconButton
                            aria-label="toggle password visibility"
                            onClick={onShowPassword}
                            onMouseDown={onMouseDownPassword}
                            edge="end"
                          >
                            {showPassword ? <VisibilityOff /> : <Visibility />}
                          </IconButton>
                        </InputAdornment>
                      ),
                    }}
                  />
                  <Button
                    className="login-page__reset-button"
                    onClick={() => onIsModalOpen(true)}
                  >
                    Forgot Password
                  </Button>
                  <Button
                    fullWidth
                    variant="outlined"
                    startIcon={<GoogleIcon />}
                    onClick={onGoogleSignIn}
                    sx={{ mt: 1.5 }}
                  >
                    Continue with Google
                  </Button>
                  <Link
                    href={ACTIVATE_ACCOUNT_ROUTE}
                    underline="hover"
                    sx={{
                      mt: 2,
                      display: 'inline-flex',
                      alignSelf: 'flex-start',
                    }}
                  >
                    Claim a phone-created account
                  </Link>
                </CardContent>
                <CardActions
                  className="login-page__card-actions"
                  sx={{
                    pl: 1,
                    pr: 1,
                    justifyContent: 'space-between',
                  }}
                >
                  <Button
                    color="error"
                    variant="outlined"
                    startIcon={<FormatClearIcon />}
                    onClick={onClear}
                  >
                    Clear
                  </Button>
                  <Button
                    disabled={disabled}
                    variant="contained"
                    startIcon={<SendIcon />}
                    onClick={onSubmit}
                  >
                    Submit
                  </Button>
                </CardActions>
              </Card>
            </LoginCardSurface>
          </PageCenteredSection>
        </PageContentShell>
      </PageViewport>
      <FooterBarComponent />
      {isModalOpen && (
        <ResetModalComponent onClose={() => onIsModalOpen(false)} />
      )}
      {mfaResolver != null && (
        <LoginMultiFactorDialog
          errorMessage={mfaErrorMessage}
          hints={mfaHints}
          isResolving={isResolvingMfa}
          isSendingCode={isSendingMfaCode}
          onClose={onCloseMultiFactorDialog}
          onConfirm={onConfirmMultiFactorCode}
          onSendCode={onSendMultiFactorCode}
          onSelectedHintUidChange={setSelectedMfaHintUid}
          onVerificationCodeChange={setMfaVerificationCode}
          recaptchaContainerId={mfaRecaptchaContainerId}
          selectedHintUid={selectedMfaHintUid}
          verificationCode={mfaVerificationCode}
          verificationId={mfaVerificationId}
        />
      )}
    </>
  )
}
